import React, { useState } from "react";
import { MdExpandMore, MdChevronRight } from "react-icons/md";
import { FiCode, FiCopy, FiCheck, FiZap, FiGift } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { serialize } from "./ruleModel";
import { Field, TextInput, Chip } from "./ui";
import RuleFlowPipeline from "./RuleFlowPipeline";

const errText = (errors, path) => errors.find((e) => e.path === path)?.message;

export default function StepSchedule({ draft, errors, onChange }) {
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopyJson = () => {
    const payload = safeSerialize(draft);
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setCopied(true);
    toast.success("Rule JSON copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <RuleFlowPipeline draft={draft} />
      <div>
        <h3 className="text-sm font-bold text-gray-900">Schedule & Execution Priority</h3>
        <p className="text-xs text-gray-500">Configure the active date window and engine execution ordering.</p>
      </div>

      {/* Date & Priority Controls */}
      <div className="grid gap-4 md:grid-cols-3 bg-gray-50/60 rounded-2xl border border-gray-200/80 p-5">
        <Field label="Start Date (Valid From)" error={errText(errors, "valid_from")}>
          <TextInput
            type="date"
            value={draft.valid_from || ""}
            onChange={(e) => onChange({ valid_from: e.target.value })}
            error={errText(errors, "valid_from")}
          />
        </Field>
        <Field label="End Date (Optional)" error={errText(errors, "valid_until")} hint="Leave blank for ongoing rule">
          <TextInput
            type="date"
            value={draft.valid_until || ""}
            onChange={(e) => onChange({ valid_until: e.target.value })}
            error={errText(errors, "valid_until")}
          />
        </Field>
        <Field label="Execution Priority" hint="Higher numbers execute earlier (e.g. 10 runs before 0)">
          <TextInput
            type="number"
            value={draft.priority ?? 0}
            onChange={(e) => onChange({ priority: Number(e.target.value) })}
          />
        </Field>
      </div>

      {/* Executive Rule Summary Card */}
      <div className="rounded-2xl border border-blue-200/80 bg-gradient-to-r from-blue-50/50 via-indigo-50/30 to-blue-50/50 p-5 space-y-3">
        <div className="flex items-center gap-2 border-b border-blue-100 pb-2">
          <FiZap className="h-4 w-4 text-blue-600" />
          <h4 className="text-xs font-bold uppercase tracking-wider text-blue-900">Executive Rule Overview</h4>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 text-xs">
          <div className="bg-white/80 rounded-xl p-3 border border-blue-100/60">
            <span className="text-gray-400 block mb-0.5">Rule Identity</span>
            <span className="font-bold text-gray-900 block">{draft.name || "Untitled Rule"}</span>
            <span className="text-gray-500 font-mono text-[11px]">Event: {draft.event_type}</span>
          </div>

          <div className="bg-white/80 rounded-xl p-3 border border-blue-100/60">
            <span className="text-gray-400 block mb-0.5">Reward Mode & Cap</span>
            <span className="font-bold text-gray-900 block capitalize">{draft.reward_mode?.replace("_", " ")}</span>
            <span className="text-gray-500 text-[11px]">
              Max triggers: {draft.max_triggers_per_user ?? "Unlimited"}
            </span>
          </div>

          <div className="bg-white/80 rounded-xl p-3 border border-blue-100/60 sm:col-span-2">
            <span className="text-gray-400 block mb-1">Actions Breakdown ({(draft.actions || []).length})</span>
            <div className="flex flex-wrap gap-2">
              {(draft.actions || []).map((a, i) => (
                <Chip key={i} color="blue">
                  <FiGift className="h-3 w-3" />
                  <span>{a.type}</span>
                  {a.amount && <span className="font-bold">+{a.amount} pts</span>}
                  {a.multiplier && <span className="font-bold">×{a.multiplier}</span>}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* JSON Inspector */}
      <div className="rounded-2xl border border-gray-200/80 bg-white overflow-hidden shadow-2xs">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setShowJson((s) => !s)}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-700 hover:text-gray-900 transition-colors"
          >
            <FiCode className="h-4 w-4 text-blue-600" />
            <span>Review Serialized Rule Payload</span>
            {showJson ? <MdExpandMore className="h-4 w-4" /> : <MdChevronRight className="h-4 w-4" />}
          </button>

          {showJson && (
            <button
              type="button"
              onClick={handleCopyJson}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {copied ? <FiCheck className="h-3.5 w-3.5 text-emerald-600" /> : <FiCopy className="h-3.5 w-3.5" />}
              <span>{copied ? "Copied" : "Copy JSON"}</span>
            </button>
          )}
        </div>

        {showJson && (
          <div className="p-4 bg-slate-900">
            <pre className="m-0 text-xs font-mono text-emerald-400 overflow-auto max-h-72 leading-relaxed">
              {JSON.stringify(safeSerialize(draft), null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function safeSerialize(draft) {
  try {
    return serialize(draft, "draft");
  } catch {
    return { error: "Incomplete configuration" };
  }
}

